import { z } from "zod";
import { trustSchema } from "./knowledge.ts";
import { projectIdSchema } from "./projects.ts";
import { userIdSchema } from "./tenancy.ts";

/**
 * Tasks (openspec change `tasks`, ADR "Tasks are work items for people"): work items for people,
 * per project. A task's title and description carry a flow label like a message; a viewer who
 * fails it gets the task as hidden, with its status, due date and assignee only. Links are
 * references (they never change who reads their target), comments are plain text.
 */

export const taskIdSchema = z.uuid().brand<"TaskId">();
export type TaskId = z.infer<typeof taskIdSchema>;

export const taskLinkIdSchema = z.uuid().brand<"TaskLinkId">();
export type TaskLinkId = z.infer<typeof taskLinkIdSchema>;

export const taskCommentIdSchema = z.uuid().brand<"TaskCommentId">();
export type TaskCommentId = z.infer<typeof taskCommentIdSchema>;

/** A fixed set: no custom workflow, and any status may follow any other. */
export const TASK_STATUSES = ["todo", "in_progress", "done", "blocked"] as const;
export const taskStatusSchema = z.enum(TASK_STATUSES);
export type TaskStatus = z.infer<typeof taskStatusSchema>;

/** Query-only unfinished work; never a stored status. */
export const taskListStatusSchema = z.enum([...TASK_STATUSES, "open"]);
export type TaskListStatus = z.infer<typeof taskListStatusSchema>;

/** Most characters (code points, after NFC) of a title, a description and a comment. */
export const MAX_TASK_TITLE_CHARS = 200;
export const MAX_TASK_DESCRIPTION_CHARS = 4000;
export const MAX_TASK_COMMENT_CHARS = 2000;
/** Most links per task. */
export const MAX_TASK_LINKS = 20;
/** Tasks per page of a list (project tasks and My tasks). */
export const TASK_PAGE_SIZE = 50;
/** Longest title search (`q`). */
export const MAX_TASK_QUERY_CHARS = 200;

const codePoints = (text: string) => Array.from(text).length;

// Control and format characters (zero-width, joiners, direction marks, overrides, isolates),
// line and paragraph separators: a title is one line that reads as it is stored.
const FORBIDDEN_IN_TITLE = /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/u;
// Multi-line text keeps tabs and line breaks, never another control character.
const FORBIDDEN_IN_TEXT = /[\p{Cc}\p{Zl}\p{Zp}]/u;
const withoutLineBreaks = (text: string) => text.replace(/[\t\n\r]/g, "");

/** NFC, then trimmed: the form a title is checked and stored in. */
export function normalizeTaskTitle(raw: string): string {
  return raw.normalize("NFC").trim();
}

/** Why a normalized title is refused, or null when it is valid. */
export function taskTitleProblem(title: string): "empty" | "too_long" | "invalid" | null {
  const length = codePoints(title);
  if (length === 0) {
    return "empty";
  }
  if (length > MAX_TASK_TITLE_CHARS) {
    return "too_long";
  }
  return FORBIDDEN_IN_TITLE.test(title) ? "invalid" : null;
}

/** A title: plain text, NFC, trimmed, 1 to 200 characters, without control or format characters. */
export const taskTitleSchema = z
  .string()
  .max(4 * MAX_TASK_TITLE_CHARS)
  .transform(normalizeTaskTitle)
  .refine((title) => taskTitleProblem(title) === null, {
    message: `A title has 1 to ${MAX_TASK_TITLE_CHARS} characters, without control characters`,
  });

/** Multi-line text: NFC, trimmed, at most `max` characters, tabs and line breaks allowed. */
const textSchema = (max: number, what: string) =>
  z
    .string()
    .max(4 * max)
    .transform((raw) => raw.normalize("NFC").trim())
    .refine((text) => codePoints(text) <= max && !FORBIDDEN_IN_TEXT.test(withoutLineBreaks(text)), {
      message: `${what} has at most ${max} characters, without control characters`,
    });

/**
 * A description: Markdown, stored as text and rendered only through the answers' sanitiser
 * (never as markup). Empty after trimming means no description.
 */
export const taskDescriptionSchema = textSchema(MAX_TASK_DESCRIPTION_CHARS, "A description");

/** A comment: plain text, 1 to 2,000 characters. */
export const taskCommentBodySchema = textSchema(MAX_TASK_COMMENT_CHARS, "A comment").refine(
  (body) => body.length > 0,
  { message: "A comment cannot be empty" },
);

/** A calendar day, `YYYY-MM-DD`. */
export const taskDueDateSchema = z.iso.date();

export const TASK_LINK_KINDS = ["conversation", "message", "document", "artefact"] as const;
export const taskLinkKindSchema = z.enum(TASK_LINK_KINDS);
export type TaskLinkKind = z.infer<typeof taskLinkKindSchema>;

/**
 * A link to add: an item of the task's project the caller can read now. A message and an
 * artefact are addressed through their conversation.
 */
export const taskLinkRequestSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("conversation"), id: z.uuid() }),
  z.object({ kind: z.literal("document"), id: z.uuid() }),
  z.object({ kind: z.literal("message"), conversationId: z.uuid(), id: z.uuid() }),
  z.object({ kind: z.literal("artefact"), conversationId: z.uuid(), id: z.uuid() }),
]);
export type TaskLinkRequest = z.infer<typeof taskLinkRequestSchema>;

/**
 * A link as one viewer sees it: its target's title when they can read the target now, else
 * `available: false` only, the same for a target they cannot read and one that is gone.
 */
export const taskLinkSchema = z.discriminatedUnion("available", [
  z.object({
    id: taskLinkIdSchema,
    kind: taskLinkKindSchema,
    available: z.literal(true),
    targetId: z.string(),
    /** The conversation of a message or an artefact (null for the other kinds). */
    conversationId: z.string().nullable(),
    /** Null for a conversation (or a message's conversation) without a title the viewer passes. */
    title: z.string().nullable(),
  }),
  z.object({ id: taskLinkIdSchema, kind: taskLinkKindSchema, available: z.literal(false) }),
]);
export type TaskLink = z.infer<typeof taskLinkSchema>;

export const taskPersonSchema = z.object({
  userId: userIdSchema,
  /** Display name, else e-mail; null once the user is unknown. */
  name: z.string().nullable(),
});
export type TaskPerson = z.infer<typeof taskPersonSchema>;

/** Optional for one canary release; writes always re-check these permissions. */
export const taskWriteBlockSchema = z.enum(["tasks_disabled", "project_frozen"]).nullable();
export const taskActionsSchema = z.object({
  editText: z.boolean(),
  editSchedule: z.boolean(),
  assign: z.boolean(),
  changeStatus: z.boolean(),
  manageLinks: z.boolean(),
  comment: z.boolean(),
  delete: z.boolean(),
  writeBlock: taskWriteBlockSchema,
  /** Work on this with ESI (tasks-conversations); absent from older servers: no control. */
  startConversation: z.boolean().optional(),
});
export type TaskActions = z.infer<typeof taskActionsSchema>;

export const taskCommentSchema = z.object({
  canDelete: z.boolean().optional(),
  id: taskCommentIdSchema,
  author: taskPersonSchema,
  body: z.string(),
  createdAt: z.iso.datetime(),
});
export type TaskComment = z.infer<typeof taskCommentSchema>;

/** Workflow fields every viewer of a task gets, hidden or not. */
const taskBaseSchema = z.object({
  id: taskIdSchema,
  projectId: projectIdSchema,
  status: taskStatusSchema,
  dueDate: taskDueDateSchema.nullable(),
  assignee: taskPersonSchema.nullable(),
  /**
   * Whether the caller may change the status now (tasks-quick-status), by the rules of detail's
   * `actions.changeStatus`. Optional for one release: absent means no row control. Grants nothing.
   */
  canChangeStatus: z.boolean().optional(),
});

/** A task whose label the viewer fails: no title, description, links or comments. */
export const hiddenTaskSchema = taskBaseSchema.extend({ hidden: z.literal(true) });

export const visibleTaskSchema = taskBaseSchema.extend({
  hidden: z.literal(false),
  title: z.string(),
  description: z.string().nullable(),
  createdBy: taskPersonSchema,
  /** The project's resolved `knowledge.defaultClassification`, read live (never stored). */
  classification: z.string(),
  trust: trustSchema,
  /** How many documents the label names (none for a task written by hand). */
  sourceCount: z.int().min(0),
  linkCount: z.int().min(0),
  commentCount: z.int().min(0),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable(),
});

/** A task in a list: visible, or hidden from this viewer. */
export const taskSummarySchema = z.discriminatedUnion("hidden", [
  visibleTaskSchema,
  hiddenTaskSchema,
]);
export type TaskSummary = z.infer<typeof taskSummarySchema>;

/** One task: a visible one adds its links (resolved for the viewer) and its comments. */
export const taskDetailSchema = z.discriminatedUnion("hidden", [
  visibleTaskSchema.extend({
    actions: taskActionsSchema.optional(),
    links: z.array(taskLinkSchema),
    comments: z.array(taskCommentSchema),
  }),
  hiddenTaskSchema.extend({ actions: taskActionsSchema.optional() }),
]);
export type TaskDetail = z.infer<typeof taskDetailSchema>;

/**
 * `POST /api/projects/:projectId/tasks`. The project comes from the path: any `projectId` in
 * the body is dropped. The assignee must hold a role in the project (`422 not_a_member`).
 */
export const createTaskRequestSchema = z.object({
  title: taskTitleSchema,
  description: taskDescriptionSchema.nullable().optional(),
  status: taskStatusSchema.optional(),
  dueDate: taskDueDateSchema.nullable().optional(),
  assigneeUserId: z.uuid().nullable().optional(),
  links: z.array(taskLinkRequestSchema).max(MAX_TASK_LINKS).optional(),
});
export type CreateTaskRequest = z.infer<typeof createTaskRequestSchema>;

/** `PATCH .../tasks/:taskId`: any subset; `null` clears the description, due date or assignee. */
export const updateTaskRequestSchema = z
  .object({
    title: taskTitleSchema.optional(),
    description: taskDescriptionSchema.nullable().optional(),
    status: taskStatusSchema.optional(),
    dueDate: taskDueDateSchema.nullable().optional(),
    assigneeUserId: z.uuid().nullable().optional(),
  })
  .refine((patch) => Object.values(patch).some((v) => v !== undefined), {
    message: "Nothing to change",
  });
export type UpdateTaskRequest = z.infer<typeof updateTaskRequestSchema>;

export const addTaskCommentRequestSchema = z.object({ body: taskCommentBodySchema });
export type AddTaskCommentRequest = z.infer<typeof addTaskCommentRequestSchema>;

const cursorSchema = z.string().min(1).max(200);

/** `GET /api/projects/:projectId/tasks`: `assignee` is `me`, `none` or a user ID. */
export const taskListQuerySchema = z.object({
  status: taskListStatusSchema.optional(),
  assignee: z.union([z.literal("me"), z.literal("none"), z.uuid()]).optional(),
  /** Matches titles the caller passes; never logged, audited or stored. */
  q: z.string().trim().min(1).max(MAX_TASK_QUERY_CHARS).optional(),
  cursor: cursorSchema.optional(),
});
export type TaskListQuery = z.infer<typeof taskListQuerySchema>;

/** Assignment-scoped discovery; names/e-mails are literal prefixes, never logged. */
export const taskAssigneeQuerySchema = z.object({
  q: z.string().trim().min(2).max(100),
  taskId: taskIdSchema.optional(),
});
export type TaskAssigneeQuery = z.infer<typeof taskAssigneeQuerySchema>;

export const taskListSchema = z.object({
  access: z.object({ canCreate: z.boolean(), writeBlock: taskWriteBlockSchema }).optional(),
  tasks: z.array(taskSummarySchema),
  nextCursor: z.string().nullable(),
});
export type TaskList = z.infer<typeof taskListSchema>;

/** `GET /api/me/tasks`: assigned to the caller, or created by them with `createdByMe=true`. */
export const myTaskListQuerySchema = z.object({
  status: taskListStatusSchema.optional(),
  createdByMe: z.enum(["true", "false"]).optional(),
  cursor: cursorSchema.optional(),
});
export type MyTaskListQuery = z.infer<typeof myTaskListQuerySchema>;

/** My tasks across projects, with the name of each project listed. */
export const myTaskListSchema = taskListSchema.extend({
  projects: z.array(z.object({ id: projectIdSchema, name: z.string() })),
});
export type MyTaskList = z.infer<typeof myTaskListSchema>;

/** A project's task figures for governance: counts only, never a title (design §8). */
export const taskCountsSchema = z.object({
  total: z.int().min(0),
  /** Not `done`. */
  open: z.int().min(0),
  /** Open with a due date before today (UTC). */
  overdue: z.int().min(0),
});
export type TaskCounts = z.infer<typeof taskCountsSchema>;

/**
 * Outcome codes of a refused task write (audit `task.refused`). Over HTTP: `422 not_a_member`,
 * `409 tasks_disabled`, `409 link_limit`, `409 project_frozen`.
 */
export const TASK_REFUSALS = ["not_a_member", "tasks_disabled", "link_limit", "frozen"] as const;
export type TaskRefusal = (typeof TASK_REFUSALS)[number];
